import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FormEvent, useState } from "react";
import { ArrowRight, KeyRound, ShieldCheck } from "lucide-react";
import { ApiError, signIn } from "../api";

function signInError(error: unknown): string {
  if (error instanceof ApiError && error.status === 401) {
    return "That email and password did not match.";
  }
  return "Yesward could not sign you in. Please try again.";
}

export function SignInPage() {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const mutation = useMutation({
    mutationFn: () => signIn(email, password),
    onSuccess: async () => {
      setPassword("");
      await queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
    },
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    mutation.mutate();
  }

  return (
    <main className="auth-shell">
      <section className="auth-intro">
        <div className="brand-lockup auth-brand">
          <span className="yesward-mark" aria-hidden="true">
            <span />
            <i />
            <span />
          </span>
          <strong>YESWARD</strong>
        </div>
        <p className="eyebrow">PROFIT-AWARE DECISIONING</p>
        <h1>Evidence before intervention.</h1>
        <p>
          Detect friction, intervene with restraint, and prove incremental gross
          profit for every client website from one controlled decision room.
        </p>
        <div className="auth-boundary">
          <ShieldCheck size={17} aria-hidden="true" />
          <span>
            <b>Tenant boundaries enforced</b>
            <small>Every website, credential and outcome remains client-scoped.</small>
          </span>
        </div>
      </section>

      <section className="auth-panel" aria-labelledby="sign-in-title">
        <div className="auth-panel-heading">
          <KeyRound size={19} aria-hidden="true" />
          <div>
            <p className="eyebrow">SECURE CONSOLE</p>
            <h2 id="sign-in-title">Sign in to Yesward</h2>
          </div>
        </div>
        <form onSubmit={submit}>
          <label>
            <span>Work email</span>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="username"
              placeholder="you@company.com"
              required
            />
          </label>
          <label>
            <span>Password</span>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              required
            />
          </label>
          {mutation.isError ? (
            <p className="form-error" role="alert">
              {signInError(mutation.error)}
            </p>
          ) : null}
          <button className="primary-action" type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? "Checking access…" : "Enter decision room"}
            <ArrowRight size={15} aria-hidden="true" />
          </button>
        </form>
        <p className="auth-footnote">
          Access is limited to approved client members. Passwords are handled by
          the Yesward control plane and are never stored in this browser.
        </p>
      </section>
    </main>
  );
}
